import React from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useSeoTitle, CONTENT_LAST_UPDATED } from "@/lib/useSeoTitle";

/**
 * PolicyPage — shared shell for trust / policy landing pages
 * (/about, /privacy, /terms, /content-policy, /changelog). Same
 * dark parchment aesthetic as SEO landings, but tuned for long-form
 * document copy: a single centered column, generous line-height, and
 * a compact table-of-contents strip under the H1.
 */
export default function PolicyPage({
  testId,
  path,
  title,
  description,
  eyebrow,
  h1,
  updated = CONTENT_LAST_UPDATED,
  toc,      // optional [{ id, label }]
  children,
}) {
  const breadcrumbs = React.useMemo(
    () => [
      { name: "Home", path: "/" },
      { name: eyebrow || h1 || "Page", path },
    ],
    [eyebrow, h1, path]
  );
  useSeoTitle({ title, description, path, breadcrumbs });

  return (
    <div data-testid={testId} className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <main id="main-content" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <header className="mb-8">
          {eyebrow && (
            <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-amber-400/80 mb-3">
              {eyebrow}
            </p>
          )}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-amber-50 leading-tight">
            {h1}
          </h1>
          <p className="mt-3 font-ui text-[10px] uppercase tracking-widest text-amber-300/50">
            Last updated {updated}
          </p>
          {Array.isArray(toc) && toc.length > 0 && (
            <nav
              aria-label="Contents"
              className="mt-6 parchment gold-border rounded-lg p-3 font-ui text-[11px] uppercase tracking-widest text-amber-300/70"
              data-testid="policy-toc"
            >
              <ul className="flex flex-wrap gap-x-4 gap-y-1">
                {toc.map((t) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`} className="hover:text-amber-100">{t.label}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </header>
        <article className="font-body text-amber-100/85 leading-relaxed text-base sm:text-lg space-y-5 prose-headings:font-display prose-headings:text-amber-50">
          {children}
        </article>
      </main>
      <Footer />
    </div>
  );
}
